import { Subscribable } from '@benzinga/subscribable';
import { safeAwaitAll, SafePromise, safeTimeout } from '@benzinga/safe-await';
import { Session } from '@benzinga/session';
import { BasicNewsRequest, BasicNewsRestfulEvent } from './request';
import {
  ArticleSearchResponse,
  BreakingNewsResponse,
  NewsInterface,
  NewsSectionInterface,
  NodeQueryParams,
  ParselyPost,
  ParselyPostParams,
  News,
  BreakingNews,
  ChartbeatPost,
  ChartbeatParams,
} from './entities';
import { SimpleNewsOptions, SimpleNewsQuery } from '@benzinga/internal-news-manager';
import { StoryObject } from '@benzinga/advanced-news-manager';

const IMG_PLACEHOLDER = 'https://cdn.benzinga.com/files/images/story/2025/07/18/bz-placeholder.png';
const BZ_PRO_IM_PLACEHOLDER = 'https://cdn.benzinga.com/files/images/story/2025/07/18/pro-placeholder.png';

const updateArticleImage = ({
  article,
  news,
}: {
  article?: StoryObject;
  news?: News;
}): StoryObject | News | undefined => {
  const expiredDate = new Date('2025-04-09T00:00:00Z');
  if (article) {
    if (article.created && new Date(article.created) < expiredDate) {
      article.image = article.isBzProPost && !article.isBzPost ? BZ_PRO_IM_PLACEHOLDER : IMG_PLACEHOLDER;
    }
    return article;
  }
  if (news) {
    if (news.created && new Date(news.created) < expiredDate) {
      news.image = IMG_PLACEHOLDER;
    }
    return news;
  }
  return;
};

export type BasicNewsManagerEvent = BasicNewsRestfulEvent;
export class BasicNewsManager extends Subscribable<BasicNewsManagerEvent> {
  private session: Session;
  private request: BasicNewsRequest;

  constructor(session: Session) {
    super();
    this.session = session;
    this.request = new BasicNewsRequest(session);
  }

  public static getName = () => 'benzinga-basic-news';

  public getTrendingNews = async (): SafePromise<News[][]> => {
    return await this.request.getTrendingNews();
  };

  public simplyQueryNews = async (
    query: SimpleNewsQuery = {},
    options: SimpleNewsOptions = {},
  ): SafePromise<StoryObject[]> => {
    const response = await this.request.simplyQueryNews(query, options);
    if (response.ok && Array.isArray(response.ok)) {
      response.ok.forEach((item: StoryObject) => {
        const article = updateArticleImage({ article: item });
        return article;
      });
      return { ok: response.ok };
    }
    return response;
  };

  public getStory = async (id: number): SafePromise<News> => {
    const response = await this.request.getStory(id);
    if (response.ok) {
      const article = updateArticleImage({ news: response.ok });
      return { ok: article as News };
    }
    return response;
  };

  public getStoriesWithIds = async (ids: number[]): SafePromise<News[]> => {
    const response = await this.request.getStoriesWithIds(ids);
    if (response.ok && Array.isArray(response.ok)) {
      response.ok.forEach((item: News) => {
        const article = updateArticleImage({ news: item });
        return article;
      });
      return { ok: response.ok };
    }
    return response;
  };

  public fetchNodes = async (query: NodeQueryParams = {}): SafePromise<News[]> => {
    const response = await this.request.fetchNodes(query);
    if (response.ok && Array.isArray(response.ok)) {
      response.ok.forEach((item: News) => {
        const article = updateArticleImage({ news: item });
        return article;
      });
      return { ok: response.ok };
    }
    return response;
  };

  public getBreakingNews = async (): SafePromise<BreakingNewsResponse> => {
    return await this.request.getBreakingNews();
  };

  public getBreakingNewsFormatted = async (): SafePromise<BreakingNews[] | null> => {
    const response = await this.getBreakingNews().then(res => {
      const events = ['breaking', 'notice', 'event', 'news'];
      const result = res?.ok?.data ?? null;
      if (Array.isArray(result)) {
        const data: BreakingNews[] = [];
        // if (result.length === 0) return;
        result.forEach(item => {
          const type = item.type.toLowerCase();
          if (events.includes(type)) {
            data.push(item);
          }
        });
        return data ?? null;
      }
      return result;
    });

    return {
      ok: response ?? null,
    };
  };

  public getLatestArticles = async (query: string): SafePromise<ArticleSearchResponse> => {
    return await this.request.getLatestArticles(query);
  };

  public getTopStoryIdsFromNodeQueue = async (): SafePromise<number[]> => {
    return await this.request.getTopStoryIdsFromNodeQueue();
  };

  public getTopStoriesFromNodeQueue = async (): SafePromise<News[]> => {
    const response = await this.getTopStoryIdsFromNodeQueue();
    if (response.ok) {
      const result = await this.getStoriesWithIds(response.ok);
      return result;
    } else if (response.err) {
      return { err: response.err };
    } else {
      return { ok: [] };
    }
  };

  public getTopPostsUsingChartbeat = async (params: ChartbeatParams = {}): SafePromise<News[]> => {
    const chartbeatResponse = await this.request.getTopPostsUsingChartbeat(params);
    if (chartbeatResponse.err) {
      return { err: chartbeatResponse.err };
    }

    if (Array.isArray(chartbeatResponse?.ok?.pages)) {
      const topPostIds = chartbeatResponse.ok.pages
        .map((topPost: ChartbeatPost) => {
          const matches = topPost.path?.match(/[0-9]{2}\/[0-9]{2}\/([0-9]+)/);
          return matches?.[1];
        })
        .filter(id => id)
        .map(id => Number(id));

      const ids = [...new Set(topPostIds)];
      const response = await this.getStoriesWithIds(ids.splice(0, params?.limit || 4));

      if (response?.ok) {
        return {
          ok: response.ok,
        };
      } else if (response?.err) {
        return { err: response.err };
      }
    }

    return { ok: [] };
  };

  public getNewsWithMultipleQueries = async (
    sections: NewsSectionInterface[],
    pageSize = 4,
  ): SafePromise<NewsInterface[]> => {
    const reqs = sections.map(section => {
      return this.queryNews(section.query, pageSize);
    });

    const response = await safeAwaitAll(reqs);

    if (response.err && response.err.length) {
      return { err: response.err[0] };
    }

    if (response.ok) {
      return {
        ok: Array.isArray(response?.ok)
          ? response.ok.map((nodes, i) => {
              return {
                nodes,
                query: sections[i].query,
                title: sections[i].title,
              } as NewsInterface;
            })
          : [],
      };
    } else {
      return { ok: [] };
    }
  };

  private queryNews = async (query: NodeQueryParams, pageSize = 4) => {
    if (!query.pageSize) {
      query.pageSize = pageSize;
    }
    if (!query.type) {
      query.type = 'stories';
    }
    return await this.fetchNodes(query);
  };
}
